import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { QrCode, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in">
        <Card className="border-border">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="flex items-center justify-center gap-2 mb-6">
              <QrCode className="text-primary" size={28} />
              <span className="font-heading text-xl font-bold">Smart-QR</span>
            </Link>
            {sent ? (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="text-emerald-500" size={28} />
                  </div>
                </div>
                <CardTitle className="font-heading text-2xl">Check your email</CardTitle>
                <CardDescription className="mt-2">
                  If an account exists with that email, we've sent a password reset link. It expires in 1 hour.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="font-heading text-2xl">Forgot password?</CardTitle>
                <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <Link to="/login">
                  <Button variant="outline" className="w-full gap-2" data-testid="back-to-login-btn">
                    <ArrowLeft size={16} /> Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="forgot-back-link">
                  <ArrowLeft size={14} className="inline mr-1" />
                  Back to Sign In
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
